import './applicantEditIncomePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { additionalIncomeTypeOptions } from '../../../store/applicantStore/constants/additionalIncomeTypeOptions';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    additionalIncomeAmount,
    additionalIncomeDurationMonths,
    additionalIncomeDurationYears,
    additionalIncomeEmployerName,
    additionalIncomeFrequency,
    additionalIncomeJobTitle,
    additionalIncomeType,
    computed: { reviewEditPathname, reviewEditProgress },
    hasAdditionalIncome,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setAdditionalIncomeAmount,
    setAdditionalIncomeDurationMonths,
    setAdditionalIncomeDurationYears,
    setAdditionalIncomeEmployerName,
    setAdditionalIncomeFrequency,
    setAdditionalIncomeJobTitle,
    setAdditionalIncomeType,
    setHasAdditionalIncome,
    syncIncome,
  } = useApplicantStore();
  const {
    clearErrors,
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      hasAdditionalIncomeValue: hasAdditionalIncome,
      incomeTypeValue: additionalIncomeType,
      incomeFrequencyValue: additionalIncomeFrequency,
      incomeAmountValue: additionalIncomeAmount,
      incomeEmployerNameValue: additionalIncomeEmployerName,
      incomeJobTitleValue: additionalIncomeJobTitle,
      incomeDurationYearsValue: additionalIncomeDurationYears,
      incomeDurationMonthsValue: additionalIncomeDurationMonths,
    },
  });

  const shouldShowIncomeFields = watch('hasAdditionalIncomeValue') === YES;

  const additionalIncomeValidation = {
    required: t('validation.additionalIncome.required'),
  };

  const handleAdditionalIncomeChange = (value: string): void => {
    if (value !== YES) {
      clearErrors([
        'incomeAmountValue',
        'incomeEmployerNameValue',
        'incomeJobTitleValue',
      ]);
    }
  };

  const incomeTypeValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.incomeType.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeFrequencyValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.incomeFrequency.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeAmountValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return { min: undefined, required: false, setValueAs: undefined };
    }

    return {
      setValueAs: (value: string): string =>
        inputEventHandler.currency.parser(value),
      required: t('validation.incomeAmount.required'),
      min: {
        value: 0,
        message: t('validation.incomeAmount.min', { min: '$0' }),
      },
    };
  }, [shouldShowIncomeFields]);

  const incomeEmployerNameValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return { required: false };
    }

    return {
      required: t('validation.employerName.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeJobTitleValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return { required: false };
    }

    return {
      required: t('validation.jobTitle.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeDurationYearsValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.durationYears.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeDurationMonthsValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.durationMonths.required'),
    };
  }, [shouldShowIncomeFields]);

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue') === IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const finishEditing = (): void => {
    navigate(reviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      hasAdditionalIncomeValue,
      incomeAmountValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeTypeValue,
    } = values;

    setHasAdditionalIncome(hasAdditionalIncomeValue);

    if (shouldShowIncomeFields) {
      setAdditionalIncomeAmount(incomeAmountValue);
      setAdditionalIncomeFrequency(incomeFrequencyValue);
      setAdditionalIncomeType(incomeTypeValue);

      if (showSecondaryEmploymentFields) {
        setAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
        setAdditionalIncomeDurationYears(incomeDurationYearsValue);
        setAdditionalIncomeEmployerName(incomeEmployerNameValue);
        setAdditionalIncomeJobTitle(incomeJobTitleValue);
      } else {
        setAdditionalIncomeDurationMonths(null);
        setAdditionalIncomeDurationYears(null);
        setAdditionalIncomeEmployerName(null);
        setAdditionalIncomeJobTitle(null);
      }
    } else {
      setAdditionalIncomeAmount(null);
      setAdditionalIncomeFrequency(null);
      setAdditionalIncomeType(null);
      setAdditionalIncomeDurationMonths(null);
      setAdditionalIncomeDurationYears(null);
      setAdditionalIncomeEmployerName(null);
      setAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncIncome();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-edit-income-page" sx={theme.pageLayout}>
        <Header
          progress={reviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.applicantEditIncome.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEditIncome.form.h1}>
                {t('ApplicantEditIncomePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEditIncome.form.h2}>
                {t('ApplicantEditIncomePage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.applicantEditIncome.alert.error}
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.applicantEditIncome.form
                        .hasAdditionalIncomeSelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditIncome.form
                        .hasAdditionalIncomeSelect
                    }
                    label={t('ApplicantEditIncomePage.additionalIncome')}
                    name="hasAdditionalIncomeValue"
                    options={additionalIncomeTypeOptions}
                    rules={additionalIncomeValidation}
                    onChange={handleAdditionalIncomeChange}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    control={control}
                    disabled={!shouldShowIncomeFields}
                    errorTestId={
                      locator.pages.applicantEditIncome.form
                        .incomeTypeSelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditIncome.form.incomeTypeSelect
                    }
                    label={t('ApplicantEditIncomePage.incomeType')}
                    name="incomeTypeValue"
                    options={incomeTypeOptions}
                    rules={incomeTypeValidation}
                  />
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      disabled={!shouldShowIncomeFields}
                      error={!isEmpty(errors.incomeEmployerNameValue)}
                      label={t('ApplicantEditIncomePage.employerName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantEditIncome.form
                              .employerNameInput,
                        },
                      }}
                      value={
                        shouldShowIncomeFields
                          ? watch(
                              'incomeEmployerNameValue',
                              additionalIncomeEmployerName || '',
                            ) || ''
                          : ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeEmployerNameValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.applicantEditIncome.form
                            .employerNameInputError
                        }
                      >
                        {toString(errors.incomeEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      disabled={!shouldShowIncomeFields}
                      error={!isEmpty(errors.incomeJobTitleValue)}
                      label={t('ApplicantEditIncomePage.jobTitle')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.applicantEditIncome.form
                              .jobTitleInput,
                        },
                      }}
                      value={
                        shouldShowIncomeFields
                          ? watch(
                              'incomeJobTitleValue',
                              additionalIncomeJobTitle || '',
                            ) || ''
                          : ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeJobTitleValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.applicantEditIncome.form
                            .jobTitleInputError
                        }
                      >
                        {toString(errors.incomeJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <Select
                        control={control}
                        disabled={!shouldShowIncomeFields}
                        errorTestId={
                          locator.pages.applicantEditIncome.form.yearSelectError
                        }
                        inputTestId={
                          locator.pages.applicantEditIncome.form.yearSelect
                        }
                        label={t('ApplicantEditIncomePage.durationYears')}
                        name="incomeDurationYearsValue"
                        options={Array.from({ length: 11 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                        rules={incomeDurationYearsValidation}
                      />
                    </div>

                    <div className="input-half">
                      <Select
                        control={control}
                        disabled={!shouldShowIncomeFields}
                        errorTestId={
                          locator.pages.applicantEditIncome.form
                            .monthSelectError
                        }
                        inputTestId={
                          locator.pages.applicantEditIncome.form.monthSelect
                        }
                        label={t('ApplicantEditIncomePage.durationMonths')}
                        name="incomeDurationMonthsValue"
                        options={Array.from({ length: 12 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                        rules={incomeDurationMonthsValidation}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    control={control}
                    disabled={!shouldShowIncomeFields}
                    errorTestId={
                      locator.pages.applicantEditIncome.form
                        .frequencySelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditIncome.form.frequencySelect
                    }
                    label={t('ApplicantEditIncomePage.incomeFrequency')}
                    name="incomeFrequencyValue"
                    options={incomeFrequencyOptions}
                    rules={incomeFrequencyValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    disabled={!shouldShowIncomeFields}
                    error={
                      shouldShowIncomeFields &&
                      !isEmpty(errors.incomeAmountValue)
                    }
                    label={t('ApplicantEditIncomePage.incomeAmount')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantEditIncome.form.amountInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    value={
                      shouldShowIncomeFields
                        ? inputEventHandler.currency.formatter(
                            watch('incomeAmountValue', additionalIncomeAmount),
                          )
                        : ''
                    }
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'incomeAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {shouldShowIncomeFields &&
                    !isEmpty(errors.incomeAmountValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.applicantEditIncome.form
                            .amountInputError
                        }
                      >
                        {toString(errors.incomeAmountValue.message)}
                      </span>
                    )}
                </div>
              </div>
              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.applicantEditIncome.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('ApplicantEditIncomePage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.applicantEditIncome.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingIncome}
                    fullWidth
                    submit
                  >
                    {t('ApplicantEditIncomePage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer data-testid={locator.pages.applicantEditIncome.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEditIncomePage';
